/**
 * YouTube Upload Service
 * Handles video uploads specifically for YouTube with S3 pre-signed URLs
 */

import { BACKEND_CONFIG } from '../config/backendConfig';

export interface YouTubeVideoFile {
  file: File;
  name: string;
  type: string;
  size: number;
}

export interface UploadedYouTubeVideo {
  filename: string;
  url: string; // S3 URL for YouTube upload
  content_type: string;
  size: number;
}

export interface YouTubeUploadResult {
  success: boolean;
  video?: UploadedYouTubeVideo;
  error?: string;
}

export interface YouTubeUploadProgress {
  stage: 'getting-url' | 'uploading' | 'complete';
  percentage: number;
  message: string;
}

class YouTubeUploadService {
  /**
   * Get pre-signed URL for YouTube video upload
   */
  private async getYouTubeUploadUrl(
    athleteId: string,
    filename: string,
    contentType: string,
    fileSize: number,
    token: string
  ) {
    const response = await fetch(`${BACKEND_CONFIG.baseURL}/profiles/athletes/${athleteId}/youtube-uploads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        filename,
        contentType,
        fileSize,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: errorText };
      }
      throw new Error(errorData.error || `HTTP ${response.status}: ${errorText}`);
    }

    return await response.json();
  }

  /**
   * Upload file directly to S3 using pre-signed URL with progress tracking
   */
  private async uploadToS3(
    file: File,
    uploadUrl: string,
    contentType: string,
    onProgress?: (progress: number) => void
  ): Promise<{ success: boolean; error?: string }> {
    console.log('📤 Starting YouTube video upload to S3');
    
    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('✅ YouTube video upload successful');
          resolve({ success: true });
        } else {
          console.error('❌ YouTube video upload failed:', xhr.status);
          resolve({ 
            success: false, 
            error: `Upload failed with status: ${xhr.status}` 
          });
        }
      };

      xhr.onerror = () => {
        console.error('💥 YouTube video upload network error');
        resolve({ success: false, error: 'Network error during upload' });
      };

      xhr.ontimeout = () => {
        console.error('⏰ YouTube video upload timeout');
        resolve({ success: false, error: 'Upload timeout' });
      };

      // Configure request for S3 upload
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', contentType);
      xhr.timeout = 600000; // 10 minutes timeout for videos

      xhr.send(file);
    });
  }

  /**
   * Upload a video for YouTube use
   */
  async uploadYouTubeVideo(
    file: YouTubeVideoFile,
    athleteId: string,
    token: string,
    onProgress?: (progress: YouTubeUploadProgress) => void
  ): Promise<YouTubeUploadResult> {
    try {
      console.log('🎥 Starting YouTube video upload:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      // Step 1: Get pre-signed URL for YouTube upload
      onProgress?.({
        stage: 'getting-url',
        percentage: 10,
        message: 'Getting upload URL...'
      });

      const urlResponse = await this.getYouTubeUploadUrl(
        athleteId,
        file.name,
        file.type,
        file.size,
        token
      );

      if (!urlResponse.success || !urlResponse.data.uploadUrl) {
        return { 
          success: false, 
          error: 'Failed to get upload URL for YouTube video' 
        };
      }

      // Step 2: Upload to S3
      onProgress?.({
        stage: 'uploading',
        percentage: 20,
        message: 'Uploading video to cloud...'
      });

      const uploadResult = await this.uploadToS3(
        file.file,
        urlResponse.data.uploadUrl,
        file.type,
        (uploadProgress) => {
          const baseProgress = 20;
          const progressRange = 80;
          const currentProgress = baseProgress + (uploadProgress / 100) * progressRange;
          
          onProgress?.({
            stage: 'uploading',
            percentage: Math.round(currentProgress),
            message: `Uploading: ${Math.round(uploadProgress)}%`
          });
        }
      );

      if (uploadResult.success) {
        console.log('✅ YouTube video uploaded successfully');
        
        onProgress?.({
          stage: 'complete',
          percentage: 100,
          message: 'Upload complete!'
        });

        return {
          success: true,
          video: {
            filename: file.name,
            url: urlResponse.data.fileUrl, // S3 URL for YouTube API
            content_type: file.type,
            size: file.size,
          }
        };
      } else {
        return {
          success: false,
          error: uploadResult.error || 'Failed to upload to S3'
        };
      }
    } catch (error) {
      console.error('❌ YouTube video upload error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Check if file type is a video
   */
  private isVideoType(mimeType: string): boolean {
    const videoTypes = [
      'video/mp4', 'video/mov', 'video/avi', 'video/quicktime', 
      'video/x-msvideo', 'video/webm', 'video/mkv'
    ];
    return videoTypes.includes(mimeType.toLowerCase());
  }

  /**
   * Validate YouTube video file
   */
  validateYouTubeVideo(file: YouTubeVideoFile): { valid: boolean; error?: string } {
    // Check if it's a video file
    if (!this.isVideoType(file.type)) {
      return { 
        valid: false, 
        error: 'File must be a video (MP4, MOV, AVI, etc.).' 
      };
    }

    // Check file size (max 256GB for YouTube, but we'll be practical)
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB practical limit
    if (file.size > maxSize) {
      return { 
        valid: false, 
        error: 'Video file too large. Maximum size is 2GB.' 
      };
    }

    // Check filename
    if (!file.name || file.name.trim().length === 0) {
      return { 
        valid: false, 
        error: 'Video must have a valid filename.' 
      };
    }

    return { valid: true };
  }

  /**
   * Get human-readable file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get supported video formats for YouTube
   */
  getSupportedFormats(): string[] {
    return [
      'video/mp4',
      'video/mov', 
      'video/quicktime',
      'video/avi',
      'video/x-msvideo',
      'video/webm',
      'video/mkv'
    ];
  }

  /**
   * Get recommended video specs for YouTube
   */
  getRecommendedSpecs() {
    return {
      maxSize: '2GB maximum',
      formats: 'MP4, MOV, AVI, WebM',
      resolution: 'Up to 4K (3840x2160)',
      fps: 'Up to 60 FPS',
      duration: 'Up to 12 hours'
    };
  }
}

export const youtubeUploadService = new YouTubeUploadService();
